import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, ExternalLink, X } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { GALLERY } from "@/data/content";

const FILTERS = [
  { id: "tutti", label: "Tutti" },
  { id: "colazioni", label: "Colazioni" },
  { id: "salati", label: "Salati" },
  { id: "spazi", label: "Spazi" },
];

export const Galleria = () => {
  const [filter, setFilter] = useState("tutti");
  const [active, setActive] = useState(null);
  const items =
    filter === "tutti" ? GALLERY : GALLERY.filter((g) => g.cat === filter);

  const close = useCallback(() => setActive(null), []);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") setActive((a) => ((a ?? 0) + 1) % items.length);
      if (e.key === "ArrowLeft") setActive((a) => ((a ?? 0) - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, items.length, close]);

  return (
    <section id="galleria" className="scroll-mt-20 bg-espresso py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <Eyebrow>Galleria</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-cream sm:text-4xl lg:text-5xl">
              L'atmosfera del Incontro
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-2">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  data-testid={`gallery-filter-btn-${f.id}`}
                  onClick={() => setFilter(f.id)}
                  className={`rounded-full border px-4 py-2 text-sm transition-all duration-300 ${
                    filter === f.id
                      ? "border-terra bg-terra text-cream"
                      : "border-sand/20 text-cream/65 hover:border-sand/45 hover:text-cream"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <motion.div layout className="mt-12 grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {items.map((g, i) => (
              <motion.button
                layout
                key={g.src}
                data-testid={`gallery-image-item-${i}`}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => setActive(i)}
                className={`group relative overflow-hidden rounded-2xl ${
                  i % 5 === 0 ? "row-span-2 h-full min-h-[280px] lg:min-h-[420px]" : "h-44 sm:h-56 lg:h-60"
                }`}
              >
                <img
                  src={g.src}
                  alt={g.caption}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-espresso/80 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <p className="absolute bottom-4 left-4 right-4 translate-y-3 text-left text-sm font-medium text-cream opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  {g.caption}
                </p>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>

        <div className="mt-8 flex justify-center">
          <a
            href="https://restaurantguru.it/Bar-Incontro-Pesaro"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="gallery-source-link"
            className="inline-flex items-center gap-2 rounded-full border border-sand/20 px-5 py-2.5 text-sm text-cream/70 transition-colors duration-300 hover:border-sand/45 hover:text-cream"
          >
            Altre foto del locale su Restaurant Guru
            <ExternalLink className="size-4" />
          </a>
        </div>
      </div>

      <AnimatePresence>
        {active !== null && items[active] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="gallery-lightbox"
            onClick={close}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-espresso/95 p-4 backdrop-blur-md sm:p-10"
          >
            <button
              onClick={close}
              data-testid="gallery-lightbox-close"
              aria-label="Chiudi"
              className="absolute right-5 top-5 inline-flex size-11 items-center justify-center rounded-full border border-cream/25 text-cream transition-colors hover:bg-cream/10"
            >
              <X className="size-5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActive((a) => ((a ?? 0) - 1 + items.length) % items.length);
              }}
              aria-label="Precedente"
              className="absolute left-4 top-1/2 z-10 inline-flex size-11 -translate-y-1/2 items-center justify-center rounded-full border border-cream/25 text-cream transition-colors hover:bg-cream/10"
            >
              <ChevronLeft className="size-5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActive((a) => ((a ?? 0) + 1) % items.length);
              }}
              aria-label="Successiva"
              className="absolute right-4 top-1/2 z-10 inline-flex size-11 -translate-y-1/2 items-center justify-center rounded-full border border-cream/25 text-cream transition-colors hover:bg-cream/10"
            >
              <ChevronRight className="size-5" />
            </button>
            <motion.figure
              key={items[active].src}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-4xl"
            >
              <img
                src={items[active].src}
                alt={items[active].caption}
                className="max-h-[78vh] w-auto rounded-xl object-contain"
              />
              <figcaption className="mt-4 flex items-center justify-between text-sm text-cream/70">
                <span>{items[active].caption}</span>
                <span className="font-mono text-xs">
                  {active + 1} / {items.length}
                </span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
